// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { Info, Lock } from 'lucide-react';
import { ClientPageSummaryBar } from '../../components/ClientPageSummaryBar';
import { WorkflowStepBody } from '../../components/WorkflowStepBody';
import { useCompanyContext } from '../../context/CompanyContext';
import { DEFAULT_PARACHUTE_EQUITY_SUB_ID, DEFAULT_PARACHUTE_WORKFLOW_SUB_ID } from '../../parachuteWorkflowSubItems';
import { DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID } from '../../documentsWorkflowSubItems';
import { TransactionInfo } from '../transaction/TransactionDetails';
import { Individuals } from '../individuals/DisqualifiedIndividuals';
import { CompensationForm } from '../compensation/CompensationForm';
import { BaseAmount } from '../baseAmount/BaseAmount';
import { ParachutePayments } from '../parachutePayments/ParachutePayments';
import { Analysis } from '../analysis/Analysis';
import { DocumentGeneration } from '../documents/DocumentGeneration';
import { build280gAnalysisResults } from '../../utils/build280gAnalysisResults';
import { build280gRunSnapshotPayload } from '../../utils/build280gRunSnapshot';
import { recordAnalysisRun } from '../../../../services/AnalysisApiClient';
import { isServerBackedAnalysisId } from '../../../../utils/isServerBackedAnalysisId';
import { useClientWorkspaceCompanyCard } from '../../hooks/useClientWorkspaceCompanyCard';
import { pushToast } from '../../../../utils/toast/toastStore';

export const WorkflowHomePage = ({
  setFormData,
  formData,
  initialStepId = 'transaction',
  /** `save`: persist draft before changing steps. `dual`: navigate only (firm review). */
  continueMode = 'save',
}) => {
  const [selectedStepId, setSelectedStepId] = useState(initialStepId);
  const [parachuteSubId, setParachuteSubId] = useState(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
  const [parachuteEquitySubId, setParachuteEquitySubId] = useState(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
  const [parachuteExpanded, setParachuteExpanded] = useState(true);
  const [documentsExpanded, setDocumentsExpanded] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculateNotice, setCalculateNotice] = useState(
    null as null | { variant: 'success' | 'danger'; text: string }
  );
  const [lastRunLabel, setLastRunLabel] = useState<string | null>(null);

  const {
    currentAnalysisId,
    currentAnalysis,
    setAnalyses,
    isCurrentAnalysisLocked,
    hasUnsavedWorkflowChanges,
    persistBasicsIfChanged,
    persistIndividualsIfChanged,
    persistCompensationIfChanged,
    persistBaseAmountIfChanged,
    persistSeveranceIfChanged,
    persistParachuteJsonSectionsIfChanged,
    documentsSubTab,
    setDocumentsSubTab,
    archivedAnalyses,
  } = useCompanyContext();

  const { showDealContextCard } = useClientWorkspaceCompanyCard(archivedAnalyses);

  const formatLastRunAt = (raw: unknown) => {
    if (raw == null) return null;
    const d = new Date(String(raw));
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const isRunStale = (() => {
    const lr = currentAnalysis?.lastRunAt;
    const wf = currentAnalysis?.workflowLastChangedAt;
    if (!wf) return false;
    if (!lr) return true;
    const tLast = new Date(String(lr)).getTime();
    const tWf = new Date(String(wf)).getTime();
    if (Number.isNaN(tLast) || Number.isNaN(tWf)) return false;
    return tLast < tWf;
  })();

  useEffect(() => {
    const analysisId = String(currentAnalysisId ?? '').trim();
    if (!isServerBackedAnalysisId(analysisId)) {
      setLastRunLabel(null);
      return;
    }
    setLastRunLabel(formatLastRunAt(currentAnalysis?.lastRunAt));
  }, [currentAnalysisId, currentAnalysis?.lastRunAt]);

  /** Match `CompanyProvider` — local parachute UI state must follow the active analysis (routed pages use context only). */
  useEffect(() => {
    if (currentAnalysisId == null) return;
    setParachuteSubId(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
    setParachuteEquitySubId(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
  }, [currentAnalysisId]);

  const handleCalculate = async () => {
    setCalculateNotice({ variant: 'success', text: 'Calculating…' });
    setIsCalculating(true);
    try {
      const analysisId = String(currentAnalysisId ?? '').trim();
      if (isCurrentAnalysisLocked) {
        setCalculateNotice({
          variant: 'danger',
          text: 'This analysis is final and cannot be edited or re-run.',
        });
        return;
      }
      if (!isServerBackedAnalysisId(analysisId)) {
        setCalculateNotice({ variant: 'danger', text: 'Please open a saved analysis first.' });
        return;
      }

      const results = build280gAnalysisResults(formData || {});
      const individualsCount = Array.isArray(results) ? results.length : 0;
      if (individualsCount <= 0) {
        setCalculateNotice({ variant: 'danger', text: 'Add at least one individual before calculating.' });
        return;
      }

      const overCount = results.filter((r) => r?.status === 'over').length;
      const status = overCount > 0 ? 'over_threshold' : 'ok';
      const snapshot = build280gRunSnapshotPayload(formData || {});
      await recordAnalysisRun(analysisId, { status, individualsCount, overCount, snapshot });

      // Backend updates `Analyses.LastRunAt` when persisting a run snapshot.
      // Keep the UI in sync without querying run history.
      const now = new Date();
      const isoUtc = now.toISOString();
      setAnalyses((prev) => prev.map((a) => (a.id === analysisId ? { ...a, lastRunAt: isoUtc } : a)));
      setLastRunLabel(formatLastRunAt(isoUtc));

      setCalculateNotice({ variant: 'success', text: 'Calculation completed and run recorded.' });
    } catch (e) {
      console.error('Failed to record analysis run', e);
      const message = e instanceof Error && e.message.trim() ? e.message.trim() : '';
      setCalculateNotice({
        variant: 'danger',
        text: message || 'Could not calculate or record analysis run. Please review inputs and try again.',
      });
    } finally {
      setIsCalculating(false);
    }
  };

  /** Same snapshot as Continue — sidebar step changes must not drop unsaved edits on Overview. */
  const persistDraftForOverview = useCallback(async () => {
    await persistBasicsIfChanged?.(formData);
    await persistIndividualsIfChanged?.(formData);
    await persistCompensationIfChanged?.(formData);
    await persistBaseAmountIfChanged?.(formData);
    await persistSeveranceIfChanged?.(formData);
    await persistParachuteJsonSectionsIfChanged?.(formData);
  }, [
    formData,
    persistBasicsIfChanged,
    persistIndividualsIfChanged,
    persistCompensationIfChanged,
    persistBaseAmountIfChanged,
    persistSeveranceIfChanged,
    persistParachuteJsonSectionsIfChanged,
  ]);

  const shouldPersistOnNavigateDefault = continueMode === 'save';

  const setActiveTab = async (tab, opts?: { persist?: boolean }) => {
    const shouldPersist = opts?.persist ?? shouldPersistOnNavigateDefault;
    if (shouldPersist) {
      try {
        await persistDraftForOverview();
      } catch (e) {
        console.error(e);
        pushToast('Could not save analysis changes. Please try again.', 'error');
        return;
      }
    }

    setSelectedStepId(tab);
    if (tab === 'parachute') {
      setParachuteSubId(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
      setParachuteEquitySubId(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
    }
    if (tab === 'documents') {
      setDocumentsSubTab(DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID);
    }
  };

  const analysisDisplayName = String(currentAnalysis?.name || '').trim() || '280G Analysis';

  const showUnsavedWorkflowNotice =
    continueMode === 'save' && !isCurrentAnalysisLocked && hasUnsavedWorkflowChanges(formData);

  const renderStepContent = () => {
    switch (selectedStepId) {
      case 'transaction':
        return (
          <TransactionInfo
            formData={formData}
            setFormData={setFormData}
            setActiveTab={setActiveTab}
            continueMode={continueMode}
          />
        );
      case 'individuals':
        return (
          <Individuals
            formData={formData}
            setFormData={setFormData}
            setActiveTab={setActiveTab}
            continueMode={continueMode}
          />
        );
      case 'compensation':
        return (
          <CompensationForm
            formData={formData}
            setFormData={setFormData}
            setActiveTab={setActiveTab}
            continueMode={continueMode}
          />
        );
      case 'baseAmount':
        return (
          <BaseAmount
            formData={formData}
            setFormData={setFormData}
            setActiveTab={setActiveTab}
            continueMode={continueMode}
          />
        );
      case 'parachute':
        return (
          <ParachutePayments
            formData={formData}
            setFormData={setFormData}
            setActiveTab={setActiveTab}
            continueMode={continueMode}
            subTab={parachuteSubId}
            setSubTab={setParachuteSubId}
            equitySubTab={parachuteEquitySubId}
            setEquitySubTab={setParachuteEquitySubId}
          />
        );
      case 'analysis':
        return (
          <Analysis
            formData={formData}
            onCalculate={handleCalculate}
            isCalculating={isCalculating}
            calculateNotice={calculateNotice}
            lastRunLabel={lastRunLabel}
            isRunStale={isRunStale}
            analysisLocked={isCurrentAnalysisLocked}
          />
        );
      case 'documents':
        return <DocumentGeneration formData={formData} subTab={documentsSubTab} />;
      default:
        return (
          <TransactionInfo
            formData={formData}
            setFormData={setFormData}
            setActiveTab={setActiveTab}
            continueMode={continueMode}
          />
        );
    }
  };

  return (
    <div className="space-y-5">
      <ClientPageSummaryBar
        formData={formData}
        intro={
          <>
            <h1 className="text-xl font-medium tracking-tight text-gray-900 sm:text-2xl">
              <span className="flex flex-wrap items-center gap-2.5">
                <span>{analysisDisplayName}</span>
                {isCurrentAnalysisLocked ? (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-red-200/90 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800 shadow-sm">
                    <Lock size={14} className="shrink-0" aria-hidden />
                    Final — Review Only
                  </span>
                ) : null}
              </span>
            </h1>
            <p>
              {showDealContextCard
                ? 'Counts below update as you complete each step. The current step is below.'
                : 'Company, closing, and counts update as you complete each step. The current step is below.'}
            </p>
          </>
        }
      />

      {showUnsavedWorkflowNotice ? (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-amber-200/90 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-950 shadow-sm"
        >
          <span className="shrink-0 text-amber-800/90" aria-hidden>
            <Info size={18} strokeWidth={2} className="mt-0.5" />
          </span>
          <p className="min-w-0 flex-1 leading-snug">
            You have unsaved changes. They will be saved when you click{' '}
            <span className="font-semibold">Save and continue</span> or switch steps in the sidebar.
          </p>
        </div>
      ) : null}

      <WorkflowStepBody
        formData={formData}
        selectedStepId={selectedStepId}
        setSelectedStepId={setSelectedStepId}
        parachuteSubId={parachuteSubId}
        setParachuteSubId={setParachuteSubId}
        parachuteEquitySubId={parachuteEquitySubId}
        setParachuteEquitySubId={setParachuteEquitySubId}
        parachuteExpanded={parachuteExpanded}
        setParachuteExpanded={setParachuteExpanded}
        documentsSubId={documentsSubTab}
        setDocumentsSubId={setDocumentsSubTab}
        documentsExpanded={documentsExpanded}
        setDocumentsExpanded={setDocumentsExpanded}
        persistBeforeNavigate={continueMode === 'save' ? persistDraftForOverview : undefined}
      >
        {renderStepContent()}
      </WorkflowStepBody>
    </div>
  );
};
